/**
 * Reading hosts out of urls, the same way the backend does.
 *
 * `BrandDomain` on the backend answers "is this cited domain the brand's own site" for the counts
 * on the detection page. These two functions answer it again in the browser, for the citation
 * chips inside an answer — the answer text arrives as raw markdown, so the domains in it were
 * never resolved server-side and there is nothing to look up.
 */

/** The Google search front door after stripping www: google.com, google.de, google.co.uk. */
const GOOGLE_SEARCH_HOST = /^google\.[a-z]{2,3}(?:\.[a-z]{2})?$/i;

/** The bare host of a url — "https://www.tenderstrike.com/de" → "tenderstrike.com". */
export const hostOf = (url) => {
  if (!url) return null;
  const value = String(url).trim();
  if (!value) return null;
  try {
    // A brand website is often stored as the user typed it, with no scheme.
    const parsed = new URL(value.includes("://") ? value : `https://${value}`);
    const host = parsed.hostname.replace(/^www\./i, "");
    return host || null;
  } catch {
    return null;
  }
};

/**
 * True when this href is Google's /goto passthrough, or a bare google.com with no page.
 * Those are not publishers — citing them would fill the source list with one fake domain.
 */
export const isGoogleGoto = (url) => {
  if (!url) return false;
  const value = String(url).trim();
  if (!value) return false;
  try {
    const parsed = new URL(value.includes("://") ? value : `https://${value}`);
    const host = parsed.hostname.replace(/^www\./i, "");
    if (!GOOGLE_SEARCH_HOST.test(host)) return false;
    const path = parsed.pathname.replace(/^\/+|\/+$/g, "");
    return path === "" || path === "goto" || path.startsWith("goto/");
  } catch {
    return /google\.[a-z.]+\/goto/i.test(value);
  }
};

/** True when a snapshot key (url or bare domain) is a publisher, not a /goto passthrough. */
export const isCitedPublisher = (value) => Boolean(value) && !isGoogleGoto(value);

/**
 * True when `host` is `own`, or a subdomain of it. The suffix test is on a label boundary, so
 * "nottenderstrike.com" is somebody else's site.
 */
export const isOwnHost = (own, host) =>
  Boolean(own && host) &&
  (host.toLowerCase() === own.toLowerCase() ||
    host.toLowerCase().endsWith(`.${own.toLowerCase()}`));
